import React, { useEffect, useState, useCallback } from "react";
import * as Icons from "lucide-react";
import api, { fmtDate } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const TYPES = [
  ["announcement", "Announcement"],
  ["update", "Company Update"],
  ["event", "Event"],
  ["training", "Training"],
];

const EMPTY = { type: "announcement", title: "", body: "", company_id: "", department: "", event_date: "", location: "", published: true };

const typeStyle = {
  announcement: "bg-blue-50 text-blue-700",
  update: "bg-slate-100 text-slate-700",
  event: "bg-emerald-50 text-emerald-700",
  training: "bg-violet-50 text-violet-700",
};

export default function AnnouncementsAdmin() {
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/announcements/manage").then(({ data }) => setItems(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    load();
    api.get("/companies").then(({ data }) => setCompanies(data)).catch(() => {});
  }, [load]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (it) => {
    setEditing(it);
    setForm({
      type: it.type, title: it.title, body: it.body || "",
      company_id: it.company_id || "", department: it.department || "",
      event_date: it.event_date ? it.event_date.slice(0, 10) : "",
      location: it.location || "", published: !!it.published,
    });
    setOpen(true);
  };

  const save = async () => {
    const isEvent = form.type === "event" || form.type === "training";
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (isEvent && !form.event_date) { toast.error("Event date is required for events and training"); return; }
    const payload = {
      type: form.type, title: form.title.trim(), body: form.body,
      company_id: form.company_id || null, department: form.department || null,
      event_date: isEvent && form.event_date ? new Date(form.event_date).toISOString() : null,
      location: form.location || null, published: form.published,
    };
    try {
      if (editing) await api.put(`/announcements/${editing.id}`, payload);
      else await api.post("/announcements", payload);
      toast.success(editing ? "Announcement updated" : "Announcement created");
      setOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
  };

  const togglePublish = async (it) => {
    try {
      await api.put(`/announcements/${it.id}`, {
        type: it.type, title: it.title, body: it.body || "",
        company_id: it.company_id || null, department: it.department || null,
        event_date: it.event_date || null, location: it.location || null,
        published: !it.published,
      });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete "${it.title}"?`)) return;
    try { await api.delete(`/announcements/${it.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const isEvent = form.type === "event" || form.type === "training";

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-medium text-slate-800">Announcements, Events & Training</div>
          <div className="text-xs text-slate-500">Publish company news and events to employees' dashboards. Publishing notifies the target audience.</div>
        </div>
        <Button onClick={openNew} className="bg-blue-600 hover:bg-blue-700" data-testid="announcement-new-btn">
          <Icons.Plus className="h-4 w-4 mr-1.5" /> New
        </Button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading && <div className="p-8 text-center text-sm text-slate-400">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="p-10 text-center text-sm text-slate-400" data-testid="announcements-admin-empty">No announcements yet. Create your first one.</div>
        )}
        {!loading && items.map((it) => (
          <div key={it.id} className="px-5 py-3.5 border-b border-slate-100 last:border-0 flex items-center gap-3" data-testid="announcement-admin-row">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className={`${typeStyle[it.type] || "bg-slate-100 text-slate-700"} hover:opacity-100 text-[10px] capitalize`}>{it.type}</Badge>
                <span className="text-sm font-medium text-slate-800 truncate">{it.title}</span>
                {!it.published && <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-200">Draft</Badge>}
              </div>
              <div className="text-[11px] text-slate-400 mt-1 flex items-center gap-2 flex-wrap">
                <span>{it.company_id ? (companies.find((c) => c.id === it.company_id)?.name || "Company") : "All companies"}</span>
                {it.department && <span>· {it.department}</span>}
                {it.event_date && <span>· Event {fmtDate(it.event_date)}</span>}
                {it.publish_date && <span>· Published {fmtDate(it.publish_date)}</span>}
              </div>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <button onClick={() => togglePublish(it)} title={it.published ? "Unpublish" : "Publish"}
                className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500" data-testid="announcement-toggle-publish">
                {it.published ? <Icons.EyeOff className="h-4 w-4" /> : <Icons.Eye className="h-4 w-4" />}
              </button>
              <button onClick={() => openEdit(it)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500" data-testid="announcement-edit">
                <Icons.Pencil className="h-4 w-4" />
              </button>
              <button onClick={() => remove(it)} className="p-1.5 rounded-lg hover:bg-rose-50 text-rose-500" data-testid="announcement-delete">
                <Icons.Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? "Edit" : "New"} Announcement</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Type</Label>
                <select className="w-full border rounded-lg h-10 px-3 text-sm" value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="announcement-type-select">
                  {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Audience (Company)</Label>
                <select className="w-full border rounded-lg h-10 px-3 text-sm" value={form.company_id}
                  onChange={(e) => setForm({ ...form, company_id: e.target.value })} data-testid="announcement-company-select">
                  <option value="">All companies</option>
                  {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="announcement-title-input" />
            </div>
            <div className="space-y-2">
              <Label>Body</Label>
              <Textarea rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="announcement-body-input" />
            </div>
            {isEvent && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Event Date</Label>
                  <Input type="date" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} data-testid="announcement-date-input" />
                </div>
                <div className="space-y-2">
                  <Label>Location</Label>
                  <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="announcement-location-input" />
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label>Department (optional)</Label>
              <Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} placeholder="Leave blank for all departments" data-testid="announcement-department-input" />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2.5">
              <div>
                <div className="text-sm font-medium text-slate-800">Published</div>
                <div className="text-xs text-slate-500">Visible on employee dashboards and notifies the audience.</div>
              </div>
              <Switch checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} data-testid="announcement-published-switch" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-blue-600 hover:bg-blue-700" data-testid="announcement-save-btn">{editing ? "Save Changes" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
